if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      resetSubmitState() {
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner').classList.add('hidden');
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const parentVariantId = formData.get('id');
        const parentQuantity = Number(formData.get('quantity') || 1);
        let sectionIds = [];
        let serviceItemsIncluded = false;

        if (this.cart) {
          sectionIds = this.cart.getSectionsToRender().map((section) => section.id);
          formData.append('sections', sectionIds);
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        /* ------------------------------------------------------------------
           FIX: one atomic add for the garment AND every embroidery service.

           Before, the Saudi icon service was added together with the garment
           but the TEXT embroidery service was added by a second, separate
           request fired after the garment was already in the cart. When that
           second request failed - sold out service variant, network blip, a
           name the sanitiser stripped to an empty string - the garment went
           through on its own and the order reached operations with no
           embroidery line and therefore no embroidery SKU (see GD106142).

           Now every service line is collected up front and sent in the same
           /cart/add.js call as the garment: they all go in, or nothing does
           and the customer is told why.
        ------------------------------------------------------------------ */
        const embroiderySelected =
          document.querySelector('saudi-icon-embroidery[data-selected="true"]') ||
          document.querySelector('initials-embroidery[data-selected="true"]') ||
          document.querySelector('.append-form-editing-wrapper[embroidary-confirmed="true"]');

        if (embroiderySelected) {
          const embroideryLinkId =
            window.crypto?.randomUUID?.() ||
            `emb-${Date.now()}-${Math.random().toString(36).slice(2)}`;

          try {
            const serviceItems = await this.collectEmbroideryServiceItems(parentVariantId, parentQuantity);

            if (!serviceItems.length) {
              throw new Error(window.embroideryStrings?.incompleteSelection ||
                'The embroidery selection is incomplete. Please select it again and try once more.');
            }

            const parentItem = {
              id: Number(parentVariantId),
              quantity: parentQuantity,
              properties: {
                '_Embroidery Link ID': embroideryLinkId
              }
            };

            for (const item of serviceItems) {
              item.properties = {
                ...(item.properties || {}),
                '_Embroidery Link ID': embroideryLinkId
              };
            }

            for (const [key, value] of formData.entries()) {
              const propertyMatch = key.match(/^properties\[(.+)\]$/);
              if (propertyMatch && value !== '') parentItem.properties[propertyMatch[1]] = value;
            }

            const saudiItems = serviceItems.filter(
              (item) => item.properties?.['Embroidery Type'] === 'Saudi Icon'
            );
            const initialsItems = serviceItems.filter(
              (item) => item.properties?.['Embroidery Type'] === 'Initials Embroidery'
            );

            if (saudiItems.length > 1) {
              parentItem.properties['Saudi Icon Placement'] = saudiItems
                .map((item) => item.properties['Saudi Icon Placement'])
                .join(' + ');
              parentItem.properties['Saudi Icon Side'] = saudiItems
                .map((item) => item.properties['Saudi Icon Side'])
                .join(' + ');
              parentItem.properties['Saudi Icon Color'] = saudiItems
                .map((item) => item.properties['Saudi Icon Color'])
                .join(' + ');
              saudiItems.forEach((item, index) => {
                parentItem.properties[`Saudi Icon ${index + 1}`] = [
                  item.properties['Saudi Icon Placement'],
                  item.properties['Saudi Icon Side'],
                  item.properties['Saudi Icon Color']
                ].join(' | ');
              });
            }

            if (initialsItems.length) {
              const initialsItem = initialsItems[0];
              parentItem.properties['Initials Embroidery'] = 'Selected / تم الاختيار';
              parentItem.properties['Initials Text'] = initialsItem.properties['Initials Text'];
              parentItem.properties['Initials Placement'] = initialsItem.properties['Initials Placement'];
              parentItem.properties['Initials Side'] = initialsItem.properties['Initials Side'];
              parentItem.properties['Initials Color'] = initialsItem.properties['Initials Color'];
              parentItem.properties['Initials Font'] = initialsItem.properties['Initials Font'];
              initialsItems.forEach((item, index) => {
                parentItem.properties[`Initials Embroidery ${index + 1}`] = [
                  item.properties['Initials Text'],
                  item.properties['Initials Placement'],
                  item.properties['Initials Side'],
                  item.properties['Initials Color'],
                  item.properties['Initials Font']
                ].join(' | ');
              });
            }

            const standardItems = serviceItems.filter(
              (item) => item.properties?.['Embroidery Type'] === 'Standard Embroidery'
            );

            if (standardItems.length) {
              const standardItem = standardItems[0];
              parentItem.properties['Standard Embroidery'] = 'Selected / تم الاختيار';
              ['First Line', 'Second Line', 'Text Placement', 'Text Color', 'Font Type'].forEach((key) => {
                if (standardItem.properties?.[key]) parentItem.properties[key] = standardItem.properties[key];
              });
            }

            /* The garment line now names the embroidery SKUs itself, so the
               operations team can still read the job off the garment even if
               a service line is later removed from the cart. */
            const serviceSkus = serviceItems.map((item) => item.sku).filter(Boolean);
            if (serviceSkus.length) {
              parentItem.properties['Embroidery SKU'] = [...new Set(serviceSkus)].join(' + ');
            }

            const sellingPlan = formData.get('selling_plan');
            if (sellingPlan) parentItem.selling_plan = Number(sellingPlan);

            config.headers['Content-Type'] = 'application/json';
            config.body = JSON.stringify({
              items: [parentItem, ...serviceItems.map(({ sku, ...item }) => item)],
              sections: sectionIds,
              sections_url: window.location.pathname
            });
            serviceItemsIncluded = true;
          } catch (error) {
            this.handleErrorMessage(
              error.message || 'The product and embroidery service could not be added. Please try again.'
            );
            this.resetSubmitState();
            return;
          }
        }

        if (!serviceItemsIncluded) config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: parentVariantId,
                errors: response.errors || response.description,
                message: response.message
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: parentVariantId,
                cartData: response
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      updateContent();
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                updateContent();
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(
              'The product could not be added to the cart. Please try again.'
            );
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      /* Builds every embroidery service line that has to accompany this
         garment: Saudi icon, initials and text embroidery. Each
         entry carries a non-cart `sku` field that is stripped before the
         request and only used to stamp the garment line. */
      async collectEmbroideryServiceItems(parentVariantId, parentQuantity) {
        const items = [];

        const selectedSaudiService = document.querySelector('saudi-icon-embroidery[data-selected="true"]');
        if (selectedSaudiService) {
          if (typeof window.getSaudiIconEmbroideryItems !== 'function') {
            throw new Error('The embroidery service is still loading. Please refresh the page and try again.');
          }

          const cartResponse = await fetch(`${window.routes.cart_url}.js`, {
            headers: { Accept: 'application/json' }
          });
          if (!cartResponse.ok) throw new Error('Could not verify the cart before adding embroidery.');

          const cartData = await cartResponse.json();
          const saudiItems = window.getSaudiIconEmbroideryItems(parentVariantId, cartData) || [];

          if (!saudiItems.length) {
            throw new Error('The embroidery selection is incomplete. Please select it again and try once more.');
          }

          items.push(...saudiItems);
        }

        const selectedInitialsService = document.querySelector('initials-embroidery[data-selected="true"]');
        if (selectedInitialsService) {
          if (typeof window.getInitialsEmbroideryItems !== 'function') {
            throw new Error('The initials embroidery service is still loading. Please refresh the page and try again.');
          }

          const initialsItems = window.getInitialsEmbroideryItems(parentVariantId, parentQuantity) || [];
          if (!initialsItems.length) {
            throw new Error('Please enter 1 to 5 English letters or full stops and complete the initials embroidery selection.');
          }

          items.push(...initialsItems);
        }

        const textEmbroideryConfirmed = document.querySelector(
          '.append-form-editing-wrapper[embroidary-confirmed="true"]'
        );
        if (textEmbroideryConfirmed) {
          if (typeof window.getTextEmbroideryItems !== 'function') {
            throw new Error('The embroidery service is still loading. Please refresh the page and try again.');
          }

          /* FIX: the text service used to be added with a hard-coded
             quantity of 1 no matter how many garments were ordered, so a
             customer buying three embroidered tops paid for - and operations
             saw - a single embroidery. It now follows the garment quantity. */
          const textItems = window.getTextEmbroideryItems(parentVariantId, parentQuantity) || [];

          if (!textItems.length) {
            throw new Error(
              window.embroideryStrings?.invalidText ||
                'Please re-enter the embroidery text. Only English letters and spaces can be embroidered.'
            );
          }

          items.push(...textItems);
        }

        return items;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const richButtonContent = this.submitButtonText.querySelector('.pdp-cart-button__content');
        const richButtonLabel = richButtonContent?.querySelector('.pdp-cart-button__label');
        let stateMessage = this.submitButtonText.querySelector('.product-form__state-message');

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (richButtonContent) {
            richButtonContent.hidden = true;
            if (!stateMessage) {
              stateMessage = document.createElement('span');
              stateMessage.className = 'product-form__state-message';
              this.submitButtonText.appendChild(stateMessage);
            }
            stateMessage.hidden = false;
            if (text) stateMessage.textContent = text;
          } else if (text) {
            this.submitButtonText.textContent = text;
          }
        } else {
          this.submitButton.removeAttribute('disabled');
          if (richButtonContent && richButtonLabel) {
            richButtonContent.hidden = false;
            if (stateMessage) stateMessage.hidden = true;
            richButtonLabel.textContent = this.submitButton.dataset.addToCartLabel || window.variantStrings.addToCart;
          } else if (document.body.getAttribute('data-template') === 'gift-card-template') {
            this.submitButtonText.textContent = window.variantStrings.giftToCart;
          } else {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
